// =====================================================================
//  What an attack looks like as it crosses the table: something travels
//  from the attacker to the card it hits, and what that is depends on the
//  attacker's affiliation (a grey slash, a black orb trailing smoke, a
//  small wave, a thrown molten rock…). The looks are in STYLES below;
//  the colors and timings are in CONFIG.combat.attackFx in src/config.ts.
// =====================================================================
import * as THREE from 'three';
import { CONFIG } from '../../config.ts';

type TexName = 'dot' | 'puff' | 'shard' | 'leaf' | 'chunk' | 'slash' | 'wave' | 'ring' | 'star' | 'arrow' | 'root' | 'bolt' | 'gust';

/** One kind of speck an attack throws off: behind it as it flies (`rate` a second), and in a burst where it lands. */
interface Speck {
  tex: TexName;
  color: number; // which of the style's colors
  rate: number; // how many a second trail behind it
  burst: number; // how many fly out where it lands
  size: number;
  life: number; // seconds
  spread: number; // how far from the middle they start
  speed: number; // how fast they drift apart
  rise: number; // up (+) or down (-) as they go
  add?: boolean; // drawn as light (glows) rather than as paint
  spin?: number; // how fast they turn
  grow?: number; // how much bigger they get over their life (1 = double)
  ground?: boolean; // they start down on the table under it, not in the air beside it (wisps, roots)
  anchor?: 'bottom' | 'top'; // the sprite hangs from its top (a lightning bolt) or stands on its bottom (a root)
  aspect?: number; // how much taller than wide (1 = square)
  float?: boolean; // they don't sink
  still?: boolean; // they don't drift sideways
  emerge?: boolean; // they grow up out of the table, then sink back as they fade (roots)
}
interface Style {
  arc: number; // how high it climbs on the way
  // what leads: a glowing or dark ball, a flat crescent or wave lying along the table, or nothing but its specks
  core: { tex: TexName; color: number; size: number; add?: boolean; flat?: boolean; spin?: number; halo?: number } | null;
  orbit?: { tex: TexName; color: number; count: number; radius: number; size: number; speed: number; add?: boolean }; // things circling it
  specks: Speck[];
  ring: number; // which color the ring where it lands is
  low?: number; // how high above the table it travels (instead of the usual height)
  arrow?: { color: number; size: number }; // what leads is an arrow, pointing the way it flies
  storm?: boolean; // it doesn't travel: it gathers over the target, then strikes (Submerged Spells)
}

const STYLES: Record<string, Style> = {
  // The Remaining: a black void ringed in deep red, red sparks whirling round it, dark red smoke and embers pouring
  // off it, and where it lands, red shards and a burst of sparks
  remaining: {
    arc: 2.2, ring: 1,
    core: { tex: 'dot', color: 0, size: 4.4, halo: 1 },
    orbit: { tex: 'dot', color: 2, count: 5, radius: 2.0, size: 1.2, speed: 13, add: true },
    specks: [
      { tex: 'puff', color: 3, rate: 46, burst: 18, size: 2.6, life: 1.0, spread: 0.5, speed: 1, rise: 1.6, grow: 1.3, spin: 2 },
      { tex: 'dot', color: 2, rate: 34, burst: 26, size: 0.9, life: 0.6, spread: 0.8, speed: 3, rise: 1, add: true },
      { tex: 'shard', color: 1, rate: 0, burst: 16, size: 1.5, life: 0.7, spread: 0.4, speed: 5, rise: 2, add: true, spin: 7 },
    ],
  },
  // Deck-Out: a ragged dark mote lifting off the empty deck, pale torn pages and dark ash streaming behind it,
  // and as it strikes its own Emperor, the pages scatter in a burst of dark red sparks
  deckOut: {
    arc: 5, ring: 1,
    core: { tex: 'dot', color: 0, size: 3.4, halo: 1 },
    orbit: { tex: 'shard', color: 2, count: 4, radius: 1.6, size: 1.1, speed: 7 },
    specks: [
      { tex: 'puff', color: 3, rate: 30, burst: 12, size: 2.2, life: 0.9, spread: 0.4, speed: 0.8, rise: 1.2, grow: 1.4, spin: 1.5 },
      { tex: 'shard', color: 2, rate: 22, burst: 22, size: 1.1, life: 0.9, spread: 0.6, speed: 4, rise: 1.6, spin: 6 },
      { tex: 'dot', color: 1, rate: 10, burst: 24, size: 0.8, life: 0.5, spread: 0.6, speed: 5, rise: 1, add: true },
    ],
  },
  // a grey slash swiping across
  neutral: {
    arc: 0.4, ring: 0,
    core: { tex: 'slash', color: 0, size: 7.6, flat: true, add: true },
    specks: [{ tex: 'dot', color: 1, rate: 26, burst: 10, size: 0.9, life: 0.35, spread: 0.9, speed: 3, rise: 0.4, add: true }],
  },
  // a black orb with smoke coming off it
  undead: {
    arc: 1.2, ring: 1,
    core: { tex: 'dot', color: 0, size: 3.4, halo: 1 },
    specks: [
      { tex: 'puff', color: 2, rate: 34, burst: 14, size: 2.6, life: 0.9, spread: 0.5, speed: 1.2, rise: 2.2, grow: 1.2, spin: 1.5 },
      { tex: 'dot', color: 1, rate: 10, burst: 8, size: 0.8, life: 0.5, spread: 0.8, speed: 3, rise: 0.5, add: true },
    ],
  },
  // pale green energy, with wisps circling it as it goes
  spectral: {
    arc: 1.4, ring: 0,
    core: { tex: 'dot', color: 0, size: 3.2, add: true },
    orbit: { tex: 'dot', color: 1, count: 3, radius: 1.7, size: 1.3, speed: 11, add: true },
    specks: [{ tex: 'puff', color: 0, rate: 30, burst: 14, size: 1.8, life: 0.7, spread: 0.6, speed: 1.6, rise: 0.9, add: true, grow: 0.8, spin: 2 }],
  },
  // a purple bolt of magic, throwing sparks
  magical: {
    arc: 1.0, ring: 0,
    core: { tex: 'star', color: 1, size: 4.4, add: true, spin: 7, halo: 0 },
    specks: [
      { tex: 'star', color: 1, rate: 30, burst: 18, size: 1.3, life: 0.6, spread: 0.7, speed: 3.4, rise: 0.6, add: true, spin: 5 },
      { tex: 'dot', color: 0, rate: 22, burst: 8, size: 1.2, life: 0.45, spread: 0.3, speed: 1, rise: 0, add: true },
    ],
  },
  // leaves and rocks, thrown
  forested: {
    arc: 2.6, ring: 0,
    core: { tex: 'chunk', color: 2, size: 2.3, spin: 9 },
    orbit: { tex: 'chunk', color: 3, count: 2, radius: 1.5, size: 1.5, speed: 6 },
    specks: [
      { tex: 'leaf', color: 0, rate: 34, burst: 16, size: 1.5, life: 0.9, spread: 1.4, speed: 2.6, rise: -1.2, spin: 7 },
      { tex: 'leaf', color: 1, rate: 16, burst: 8, size: 1.2, life: 0.8, spread: 1.4, speed: 2.6, rise: -0.8, spin: -6 },
      { tex: 'chunk', color: 3, rate: 0, burst: 8, size: 1.1, life: 0.6, spread: 0.4, speed: 5, rise: 2.5, spin: 8 },
    ],
  },
  // a small wave rolling forward and crashing
  submerged: {
    arc: 0, ring: 1,
    core: { tex: 'wave', color: 0, size: 9, flat: true },
    specks: [
      { tex: 'dot', color: 1, rate: 40, burst: 26, size: 0.85, life: 0.55, spread: 2.2, speed: 3.2, rise: 3, add: true },
      { tex: 'puff', color: 1, rate: 14, burst: 10, size: 2.2, life: 0.6, spread: 1.6, speed: 1.6, rise: 0.6, add: true, grow: 0.8 },
    ],
  },
  // a small sandstorm sweeping forward
  duned: {
    arc: 0.5, ring: 0,
    core: null,
    orbit: { tex: 'puff', color: 0, count: 7, radius: 2.1, size: 3.0, speed: 9 },
    specks: [
      { tex: 'puff', color: 1, rate: 44, burst: 18, size: 2.4, life: 0.75, spread: 1.6, speed: 2.4, rise: 1.4, grow: 0.9, spin: 3 },
      { tex: 'dot', color: 2, rate: 40, burst: 20, size: 0.55, life: 0.5, spread: 2.2, speed: 4, rise: 1 },
    ],
  },
  // a red-hot molten rock, lobbed
  volcanic: {
    arc: 3.4, ring: 1,
    core: { tex: 'chunk', color: 0, size: 3.0, spin: 6, halo: 1 },
    specks: [
      { tex: 'dot', color: 1, rate: 44, burst: 26, size: 1.1, life: 0.6, spread: 0.5, speed: 2.6, rise: 1.6, add: true },
      { tex: 'puff', color: 2, rate: 14, burst: 8, size: 2.2, life: 0.8, spread: 0.4, speed: 1, rise: 2.4, grow: 1.1 },
    ],
  },
  // a singularity: a small dark blue orb pulling purple and grey clouds round and round it, collapsing in a burst where it lands
  astral: {
    arc: 1.6, ring: 1,
    core: { tex: 'dot', color: 0, size: 3.6, halo: 1 },
    orbit: { tex: 'puff', color: 1, count: 4, radius: 1.9, size: 2.2, speed: 12, add: true },
    specks: [
      { tex: 'puff', color: 2, rate: 30, burst: 16, size: 2.2, life: 0.8, spread: 0.7, speed: 1.0, rise: 0.4, grow: 1.0, spin: 4 },
      { tex: 'puff', color: 1, rate: 22, burst: 14, size: 1.8, life: 0.7, spread: 0.9, speed: 1.4, rise: 0.3, add: true, grow: 0.8, spin: -5 },
      { tex: 'dot', color: 3, rate: 18, burst: 18, size: 0.7, life: 0.5, spread: 1.0, speed: 3.2, rise: 0.4, add: true },
    ],
  },
  // ice and snow
  frigid: {
    arc: 0.8, ring: 0,
    core: { tex: 'shard', color: 0, size: 3.6, add: true, halo: 1 },
    orbit: { tex: 'shard', color: 1, count: 3, radius: 1.4, size: 2.0, speed: 5, add: true },
    specks: [
      { tex: 'dot', color: 2, rate: 46, burst: 22, size: 0.7, life: 0.8, spread: 1.5, speed: 1.8, rise: -1.4 },
      { tex: 'shard', color: 1, rate: 12, burst: 12, size: 1.3, life: 0.5, spread: 0.6, speed: 4.4, rise: 1.2, add: true, spin: 6 },
    ],
  },
};

/** The looks of Spells reaching their targets (CONFIG.combat.spellFx). */
const SPELL_STYLES: Record<string, Style> = {
  // an arrow loosed high, arcing down onto the target
  neutral: {
    arc: 5.5, ring: 1,
    core: null,
    arrow: { color: 0, size: 12 },
    specks: [
      { tex: 'dot', color: 1, rate: 22, burst: 10, size: 0.6, life: 0.35, spread: 0.2, speed: 0.6, rise: 0, add: true },
      { tex: 'chunk', color: 2, rate: 0, burst: 9, size: 0.7, life: 0.55, spread: 0.3, speed: 4, rise: 3, spin: 9 },
    ],
  },
  // dark energy, red and green flickering about it, a little smoke behind, lobbed in an arc
  undead: {
    arc: 3.2, ring: 1,
    core: { tex: 'dot', color: 0, size: 3.6, halo: 1 },
    orbit: { tex: 'dot', color: 3, count: 2, radius: 1.3, size: 1.1, speed: 9, add: true },
    specks: [
      { tex: 'puff', color: 2, rate: 18, burst: 12, size: 1.8, life: 0.7, spread: 0.3, speed: 0.6, rise: 1.2, grow: 0.9, spin: 1.5 },
      { tex: 'dot', color: 1, rate: 16, burst: 12, size: 0.8, life: 0.45, spread: 0.7, speed: 2.4, rise: 0.4, add: true },
      { tex: 'dot', color: 3, rate: 10, burst: 10, size: 0.7, life: 0.45, spread: 0.7, speed: 2.4, rise: 0.4, add: true },
    ],
  },
  // a purple bolt of magic with pink sparks
  magical: {
    arc: 1.8, ring: 1,
    core: { tex: 'star', color: 0, size: 4.8, add: true, spin: 8, halo: 0 },
    orbit: { tex: 'star', color: 1, count: 3, radius: 1.5, size: 1.4, speed: 10, add: true },
    specks: [
      { tex: 'star', color: 1, rate: 34, burst: 22, size: 1.2, life: 0.6, spread: 0.6, speed: 3.2, rise: 0.6, add: true, spin: 5 },
      { tex: 'dot', color: 0, rate: 24, burst: 10, size: 1.3, life: 0.45, spread: 0.3, speed: 1, rise: 0, add: true },
      { tex: 'dot', color: 2, rate: 8, burst: 10, size: 0.6, life: 0.5, spread: 0.5, speed: 3, rise: 1, add: true },
    ],
  },
  // ghostly energy skimming low, wisps rising up off the table all along its way
  spectral: {
    arc: 0.6, ring: 0, low: 0.9,
    core: { tex: 'dot', color: 0, size: 3.0, add: true, halo: 2 },
    specks: [
      { tex: 'puff', color: 1, rate: 46, burst: 18, size: 1.7, life: 0.9, spread: 0.9, speed: 0.4, rise: 3.4, add: true, grow: 0.6, spin: 2, ground: true, float: true, aspect: 1.6 },
      { tex: 'dot', color: 0, rate: 24, burst: 10, size: 0.7, life: 0.7, spread: 1.0, speed: 0.4, rise: 4, add: true, ground: true, float: true },
    ],
  },
  // a small sandstorm, whirling its way over
  duned: {
    arc: 0.4, ring: 0, low: 1.2,
    core: null,
    orbit: { tex: 'puff', color: 0, count: 10, radius: 2.6, size: 3.2, speed: 11 },
    specks: [
      { tex: 'puff', color: 1, rate: 54, burst: 22, size: 2.6, life: 0.8, spread: 2.0, speed: 2.4, rise: 1.6, grow: 0.9, spin: 4 },
      { tex: 'dot', color: 2, rate: 50, burst: 26, size: 0.5, life: 0.5, spread: 2.6, speed: 4.4, rise: 1 },
    ],
  },
  // roots break up out of the table one after another all the way to the target, and burst up around it
  forested: {
    arc: 0, ring: 0, low: 0,
    core: null,
    specks: [
      { tex: 'root', color: 0, rate: 40, burst: 14, size: 2.2, life: 0.9, spread: 0.8, speed: 0, rise: 0, ground: true, anchor: 'bottom', aspect: 2.3, float: true, still: true, emerge: true },
      { tex: 'root', color: 1, rate: 22, burst: 8, size: 1.7, life: 0.8, spread: 1.1, speed: 0, rise: 0, ground: true, anchor: 'bottom', aspect: 2.7, float: true, still: true, emerge: true },
      { tex: 'chunk', color: 3, rate: 20, burst: 14, size: 0.6, life: 0.5, spread: 0.6, speed: 2, rise: 2.4, spin: 6, ground: true },
      { tex: 'leaf', color: 2, rate: 0, burst: 8, size: 1.1, life: 0.8, spread: 0.8, speed: 2.4, rise: 2, spin: 6 },
    ],
  },
  // a thunderstorm gathers over the target, and lightning strikes it
  submerged: {
    arc: 0, ring: 2, storm: true,
    core: null,
    specks: [
      { tex: 'puff', color: 0, rate: 130, burst: 10, size: 5.4, life: 1.2, spread: 2.8, speed: 0.5, rise: 0, grow: 0.6, spin: 0.6, float: true },
      { tex: 'puff', color: 3, rate: 50, burst: 0, size: 4.0, life: 1.1, spread: 2.2, speed: 0.4, rise: 0, grow: 0.5, float: true },
      { tex: 'dot', color: 2, rate: 60, burst: 40, size: 0.4, life: 0.45, spread: 2.4, speed: 0, rise: -14, aspect: 5, float: true, still: true, add: true },
      { tex: 'bolt', color: 1, rate: 3, burst: 3, size: 2.2, life: 0.5, spread: 0.4, speed: 0, rise: 0, anchor: 'top', aspect: 2.4, float: true, still: true, add: true },
      { tex: 'dot', color: 1, rate: 0, burst: 2, size: 7, life: 0.25, spread: 0, speed: 0, rise: 0, float: true, still: true, add: true },
    ],
  },
  // a wave of lava rolls in, and a plume of it bursts up as it hits
  volcanic: {
    arc: 0, ring: 0, low: 0.4,
    core: { tex: 'wave', color: 0, size: 8, flat: true, add: true },
    specks: [
      { tex: 'dot', color: 1, rate: 52, burst: 0, size: 0.9, life: 0.6, spread: 2.0, speed: 1.6, rise: 2.2, add: true },
      { tex: 'puff', color: 2, rate: 16, burst: 14, size: 2.2, life: 0.9, spread: 1.2, speed: 0.6, rise: 4, grow: 1.2 },
      { tex: 'chunk', color: 0, rate: 0, burst: 30, size: 1.4, life: 1.1, spread: 0.5, speed: 1.4, rise: 15, spin: 7 },
      { tex: 'dot', color: 1, rate: 0, burst: 40, size: 1.8, life: 1.0, spread: 0.5, speed: 1.6, rise: 14, add: true },
      { tex: 'dot', color: 0, rate: 0, burst: 20, size: 3.0, life: 0.6, spread: 0.3, speed: 0.8, rise: 9, add: true, grow: 0.5 },
    ],
  },
  // a flaming meteor falling out of the sky onto its target, trailing fire and purple sparkles, bursting where it lands
  astral: {
    arc: 9, ring: 1,
    core: { tex: 'chunk', color: 3, size: 3.2, spin: 5, halo: 0 },
    specks: [
      { tex: 'dot', color: 0, rate: 70, burst: 30, size: 1.6, life: 0.5, spread: 0.4, speed: 1.2, rise: 1.2, add: true, grow: 0.6 },
      { tex: 'dot', color: 1, rate: 50, burst: 24, size: 1.0, life: 0.45, spread: 0.5, speed: 2.0, rise: 1.6, add: true },
      { tex: 'star', color: 2, rate: 36, burst: 26, size: 1.1, life: 0.7, spread: 0.8, speed: 2.6, rise: 0.8, add: true, spin: 6 },
      { tex: 'puff', color: 4, rate: 16, burst: 12, size: 2.2, life: 0.9, spread: 0.4, speed: 0.8, rise: 2, grow: 1.1 },
      { tex: 'chunk', color: 3, rate: 0, burst: 14, size: 1.0, life: 0.8, spread: 0.4, speed: 4.4, rise: 6, spin: 8 },
    ],
  },
  // a gust of light blue freezing wind, frost and snow blowing along with it
  frigid: {
    arc: 0.8, ring: 0,
    core: { tex: 'gust', color: 0, size: 11, flat: true, add: true },
    specks: [
      { tex: 'dot', color: 2, rate: 70, burst: 26, size: 0.7, life: 0.9, spread: 2.2, speed: 1.4, rise: -0.6 },
      { tex: 'puff', color: 0, rate: 44, burst: 18, size: 2.8, life: 0.7, spread: 1.8, speed: 1.2, rise: 0.3, add: true, grow: 0.9 },
      { tex: 'shard', color: 1, rate: 0, burst: 14, size: 1.2, life: 0.5, spread: 0.5, speed: 4.4, rise: 1.2, add: true, spin: 6 },
    ],
  },
};

function canvasTex(w: number, h: number, draw: (ctx: CanvasRenderingContext2D) => void) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  draw(c.getContext('2d')!);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
let texes: Record<TexName, THREE.Texture> | null = null;
/** The shapes the specks are cut from, drawn once in white so each can be tinted. */
function textures(): Record<TexName, THREE.Texture> {
  if (texes) return texes;
  const dot = canvasTex(64, 64, (ctx) => {
    const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.45, 'rgba(255,255,255,0.75)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 64, 64);
  });
  const puff = canvasTex(128, 128, (ctx) => {
    let seed = 11;
    const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
    for (let i = 0; i < 9; i++) {
      const x = 64 + (rnd() - 0.5) * 50, y = 64 + (rnd() - 0.5) * 50, r = 22 + rnd() * 20;
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, 'rgba(255,255,255,0.5)');
      g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 128, 128);
    }
  });
  const poly = (pts: [number, number][], soft = false) =>
    canvasTex(64, 64, (ctx) => {
      ctx.beginPath();
      pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
      ctx.closePath();
      const g = ctx.createLinearGradient(0, 0, 64, 64);
      g.addColorStop(0, '#ffffff');
      g.addColorStop(1, soft ? '#9a9a9a' : '#c9c9c9');
      ctx.fillStyle = g;
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.35)';
      ctx.lineWidth = 2;
      ctx.stroke();
    });
  const shard = poly([[32, 2], [44, 30], [32, 62], [20, 30]]);
  const chunk = poly([[14, 12], [38, 4], [58, 22], [52, 48], [30, 60], [8, 44]], true);
  const leaf = canvasTex(64, 64, (ctx) => {
    ctx.beginPath();
    ctx.moveTo(8, 56);
    ctx.quadraticCurveTo(6, 14, 56, 8);
    ctx.quadraticCurveTo(52, 56, 8, 56);
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.3)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(8, 56);
    ctx.lineTo(50, 14);
    ctx.stroke();
  });
  // a crescent, its bright edge leading (up, in the picture)
  const slash = canvasTex(256, 128, (ctx) => {
    for (let i = 0; i < 3; i++) {
      ctx.beginPath();
      ctx.arc(128, 150 + i * 16, 118 - i * 4, Math.PI * 1.12, Math.PI * 1.88);
      ctx.strokeStyle = `rgba(255,255,255,${[1, 0.45, 0.2][i]})`;
      ctx.lineWidth = [9, 12, 16][i];
      ctx.lineCap = 'round';
      ctx.stroke();
    }
  });
  // a wave seen from above: a foaming crest (up, in the picture) with thinner water behind it
  const wave = canvasTex(256, 128, (ctx) => {
    let seed = 5;
    const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
    const body = ctx.createLinearGradient(0, 20, 0, 128);
    body.addColorStop(0, 'rgba(255,255,255,0.95)');
    body.addColorStop(0.3, 'rgba(255,255,255,0.6)');
    body.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = body;
    ctx.beginPath();
    ctx.moveTo(6, 128);
    ctx.quadraticCurveTo(20, 30, 128, 22);
    ctx.quadraticCurveTo(236, 30, 250, 128);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#fff';
    for (let i = 0; i < 46; i++) {
      const x = 22 + rnd() * 212;
      const edge = 22 + Math.pow(Math.abs(x - 128) / 112, 2) * 60;
      ctx.beginPath();
      ctx.arc(x, edge + rnd() * 12 - 4, 3 + rnd() * 7, 0, Math.PI * 2);
      ctx.fill();
    }
  });
  const ring = canvasTex(128, 128, (ctx) => {
    const g = ctx.createRadialGradient(64, 64, 34, 64, 64, 62);
    g.addColorStop(0, 'rgba(255,255,255,0)');
    g.addColorStop(0.6, 'rgba(255,255,255,1)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 128, 128);
  });
  const star = canvasTex(64, 64, (ctx) => {
    const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 14);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 64, 64);
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const r = i % 2 ? 5 : 30;
      ctx.lineTo(32 + Math.cos(a) * r, 32 + Math.sin(a) * r);
    }
    ctx.closePath();
    ctx.fill();
  });
  // an arrow, its head at the top of the picture
  const arrow = canvasTex(64, 256, (ctx) => {
    ctx.fillStyle = '#8a6a44';
    ctx.fillRect(29, 40, 6, 180);
    ctx.fillStyle = '#e8eef4';
    ctx.beginPath(); ctx.moveTo(32, 2); ctx.lineTo(48, 46); ctx.lineTo(32, 38); ctx.lineTo(16, 46); ctx.closePath(); ctx.fill();
    ctx.fillStyle = '#f4f4f4';
    for (const sx of [-1, 1]) { ctx.beginPath(); ctx.moveTo(32, 196); ctx.lineTo(32 + sx * 16, 214); ctx.lineTo(32 + sx * 16, 252); ctx.lineTo(32, 236); ctx.closePath(); ctx.fill(); }
  });
  // a root, thick at its foot (the bottom of the picture), curling to a point
  const root = canvasTex(64, 128, (ctx) => {
    ctx.beginPath();
    ctx.moveTo(14, 128);
    ctx.bezierCurveTo(16, 80, 40, 70, 30, 30);
    ctx.quadraticCurveTo(28, 12, 38, 2);
    ctx.quadraticCurveTo(44, 20, 42, 36);
    ctx.bezierCurveTo(56, 70, 40, 92, 50, 128);
    ctx.closePath();
    const g = ctx.createLinearGradient(0, 0, 64, 0);
    g.addColorStop(0, '#d0d0d0'); g.addColorStop(0.5, '#ffffff'); g.addColorStop(1, '#a8a8a8');
    ctx.fillStyle = g;
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.35)';
    ctx.lineWidth = 2;
    ctx.stroke();
  });
  // a lightning bolt, hanging from the top of the picture
  const bolt = canvasTex(64, 256, (ctx) => {
    const pts: [number, number][] = [[32, 0], [22, 70], [40, 90], [18, 170], [36, 186], [24, 256]];
    for (const [w, a] of [[14, 0.25], [7, 0.6], [3, 1]] as const) {
      ctx.beginPath();
      pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
      ctx.strokeStyle = `rgba(255,255,255,${a})`;
      ctx.lineWidth = w;
      ctx.lineJoin = 'round';
      ctx.stroke();
    }
  });
  // streaks of wind, brightest at the front (the top of the picture)
  const gust = canvasTex(256, 128, (ctx) => {
    let seed = 7;
    const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
    for (let i = 0; i < 14; i++) {
      const x = 30 + rnd() * 196, top = 6 + rnd() * 40, len = 50 + rnd() * 70;
      const g = ctx.createLinearGradient(0, top, 0, top + len);
      g.addColorStop(0, 'rgba(255,255,255,0.95)');
      g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.strokeStyle = g;
      ctx.lineWidth = 2 + rnd() * 4;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(x, top);
      ctx.quadraticCurveTo(x + (rnd() - 0.5) * 30, top + len / 2, x + (rnd() - 0.5) * 20, top + len);
      ctx.stroke();
    }
  });
  texes = { dot, puff, shard, leaf, chunk, slash, wave, ring, star, arrow, root, bolt, gust };
  return texes;
}

interface Particle { sp: THREE.Sprite; born: number; life: number; vx: number; vy: number; vz: number; size: number; grow: number; spin: number; fade: number; aspect: number; float: boolean; emerge: boolean }
interface Shot {
  style: Style; colors: string[];
  from: THREE.Vector3; to: THREE.Vector3;
  t0: number; dur: number; miss: boolean;
  group: THREE.Group; core: THREE.Object3D | null; halo: THREE.Sprite | null; orbit: THREE.Sprite[];
  owed: number[]; last: number; landed: boolean; onLand?: () => void;
  arrow: THREE.Object3D | null;
}

export class AttackFx {
  private scene: THREE.Scene;
  private shots: Shot[] = [];
  private parts: Particle[] = [];
  private rings: { mesh: THREE.Mesh; born: number; life: number; size: number }[] = [];
  private waves: { t0: number; dur: number; dir: THREE.Vector3; side: THREE.Vector3; start: THREE.Vector3; travel: number; across: number; crest: THREE.Mesh[]; colors: string[]; owed: number; bolts: number[]; last: number }[] = [];
  private lastTick = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  /** The looks an attack can have (affiliation names, and "neutral"). */
  static has(kind: string) {
    return kind in STYLES;
  }

  /**
   * Sends an attack across the table: after `delay` seconds it leaves `from`, and `seconds` later it lands on
   * `to` (onLand is called then). A miss sails wide and fades without landing.
   */
  launch(from: THREE.Vector3, to: THREE.Vector3, kind: string, delay: number, seconds: number, miss = false, onLand?: () => void, spell = false) {
    const set = spell ? SPELL_STYLES : STYLES;
    const style = set[kind] ?? set.neutral;
    const Z = CONFIG.combat.attackFx.size;
    const colors = (spell ? CONFIG.combat.spellFx.colors : CONFIG.combat.attackFx.colors)[kind in set ? kind : 'neutral'] ?? ['#ffffff'];
    const end = to.clone();
    if (miss) {
      // wide of the mark: past the target and off to one side
      const dir = to.clone().sub(from).setY(0).normalize();
      end.add(dir.clone().multiplyScalar(5)).add(new THREE.Vector3(-dir.z, 0, dir.x).multiplyScalar(4.5));
    }
    const group = new THREE.Group();
    group.visible = false;
    const t = textures();
    const col = (i: number) => colors[Math.min(i, colors.length - 1)];
    let core: THREE.Object3D | null = null;
    let halo: THREE.Sprite | null = null;
    const c = style.core;
    if (c) {
      if (c.flat) {
        // lying along the table, its leading edge toward the target
        const geo = new THREE.PlaneGeometry(c.size * Z, (c.size * Z) / 2);
        geo.rotateX(-Math.PI / 2);
        const mesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ map: t[c.tex], color: col(c.color), transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: c.add ? THREE.AdditiveBlending : THREE.NormalBlending }));
        mesh.rotation.y = Math.atan2(-(end.x - from.x), -(end.z - from.z));
        core = mesh;
      } else {
        const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: t[c.tex], color: col(c.color), transparent: true, depthWrite: false, blending: c.add ? THREE.AdditiveBlending : THREE.NormalBlending }));
        sp.scale.set(c.size * Z, c.size * Z, 1);
        core = sp;
      }
      if (c.halo !== undefined) {
        halo = new THREE.Sprite(new THREE.SpriteMaterial({ map: t.dot, color: col(c.halo), transparent: true, opacity: 0.8, depthWrite: false, blending: THREE.AdditiveBlending }));
        halo.scale.set(c.size * Z * 2.1, c.size * Z * 2.1, 1);
        group.add(halo);
      }
      core.renderOrder = 21;
      group.add(core);
    }
    const orbit: THREE.Sprite[] = [];
    if (style.orbit) {
      for (let i = 0; i < style.orbit.count; i++) {
        const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: t[style.orbit.tex], color: col(style.orbit.color), transparent: true, depthWrite: false, blending: style.orbit.add ? THREE.AdditiveBlending : THREE.NormalBlending }));
        sp.scale.set(style.orbit.size * Z, style.orbit.size * Z, 1);
        sp.renderOrder = 20;
        group.add(sp);
        orbit.push(sp);
      }
    }
    // an arrow: two crossed planes along its length (+Z), turned each frame to point the way it is flying
    let arrow: THREE.Object3D | null = null;
    if (style.arrow) {
      arrow = new THREE.Group();
      const len = style.arrow.size * Z;
      for (const turn of [0, Math.PI / 2]) {
        const geo = new THREE.PlaneGeometry(len / 3, len);
        geo.rotateX(Math.PI / 2);
        geo.rotateZ(turn);
        const m = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ map: t.arrow, color: col(style.arrow.color), transparent: true, depthWrite: false, side: THREE.DoubleSide }));
        m.renderOrder = 21;
        arrow.add(m);
      }
      group.add(arrow);
    }
    this.scene.add(group);
    const now = performance.now() / 1000;
    this.shots.push({ style, colors, from: from.clone(), to: end, t0: now + delay, dur: Math.max(0.1, seconds), miss, group, core, halo, orbit, owed: style.specks.map(() => 0), last: now + delay, landed: false, onLand, arrow });
  }

  /** A card rising up out of the table: ripples of water spreading from it, and spray (a Leviathan Body Segment). */
  emerge(at: THREE.Vector3, delay = 0) {
    const E = CONFIG.combat.emerge;
    const now = performance.now() / 1000 + delay;
    const Z = CONFIG.combat.attackFx.size;
    const A = CONFIG.combat.attackFx;
    for (let i = 0; i < 4; i++) {
      const geo = new THREE.PlaneGeometry(1, 1);
      geo.rotateX(-Math.PI / 2);
      const mesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ map: textures().ring, color: E.colors[i % 2], transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }));
      mesh.position.set(at.x, at.y + 0.3, at.z);
      mesh.renderOrder = 18;
      mesh.scale.set(0.01, 1, 0.01);
      this.scene.add(mesh);
      this.rings.push({ mesh, born: now + i * 0.22, life: A.ringSeconds * 2.2, size: A.ringSize * (1.2 + i * 0.25) });
    }
    const shot = { colors: E.colors } as unknown as Shot;
    const spray: Speck = { tex: 'dot', color: 1, rate: 0, burst: 0, size: 0.8, life: 0.8, spread: 2.4, speed: 2.2, rise: 5, add: true };
    const foam: Speck = { tex: 'puff', color: 0, rate: 0, burst: 0, size: 2.4, life: 0.9, spread: 2.2, speed: 1.4, rise: 1.4, add: true, grow: 1 };
    setTimeout(() => {
      const t2 = performance.now() / 1000;
      for (let n = 0; n < 28; n++) this.speck(shot, spray, at, t2, true);
      for (let n = 0; n < 12; n++) this.speck(shot, foam, at, t2, true);
    }, delay * 1000);
    void Z;
  }

  /**
   * Primordial Seastorm: a great wave rolls across the whole table from one side (`dir` 0–3: from the left, the
   * right, the near or the far side), foam and spray breaking off its crest, while red lightning strikes here and
   * there. colors: the water, the foam, the lightning, its glow.
   */
  seastorm(dir: number, width: number, depth: number, colors: string[], seconds = 2.6) {
    const dirs = [new THREE.Vector3(1, 0, 0), new THREE.Vector3(-1, 0, 0), new THREE.Vector3(0, 0, -1), new THREE.Vector3(0, 0, 1)];
    const d = dirs[((dir % 4) + 4) % 4];
    const along = d.x !== 0 ? width : depth;
    const across = (d.x !== 0 ? depth : width) + 16;
    const travel = along + 30;
    const start = d.clone().multiplyScalar(-travel / 2);
    const side = new THREE.Vector3(-d.z, 0, d.x);
    const crest: THREE.Mesh[] = [];
    // the wall of water: a few bands lying along the front, the leading one brightest, lifted toward the crest
    for (let i = 0; i < 3; i++) {
      const geo = new THREE.PlaneGeometry(across, 7 - i * 1.5);
      geo.rotateX(-Math.PI / 2);
      const mesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ map: textures().wave, color: colors[i === 0 ? 1 : 0], transparent: true, opacity: 0, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending }));
      mesh.rotation.y = Math.atan2(-d.x, -d.z);
      mesh.renderOrder = 19;
      this.scene.add(mesh);
      crest.push(mesh);
    }
    const now = performance.now() / 1000;
    const bolts = Array.from({ length: 7 }, (_, i) => 0.12 + (i / 7) * 0.75 + Math.random() * 0.08);
    this.waves.push({ t0: now, dur: seconds, dir: d, side, start, travel, across, crest, colors, owed: 0, bolts, last: now });
  }

  private updateWaves(now: number) {
    const Z = CONFIG.combat.attackFx.size;
    for (const w of this.waves) {
      const k = Math.min(1, (now - w.t0) / w.dur);
      const front = w.start.clone().addScaledVector(w.dir, w.travel * k);
      const swell = Math.sin(Math.PI * k);
      w.crest.forEach((m, i) => {
        m.position.copy(front).addScaledVector(w.dir, -i * 4.5);
        m.position.y = 1.2 + (3 - i) * 0.9 * swell;
        (m.material as THREE.MeshBasicMaterial).opacity = Math.min(1, (0.95 - i * 0.25) * Math.min(1, swell * 2.2));
      });
      const shot = { colors: w.colors } as unknown as Shot;
      // foam and spray breaking off the crest, all along it
      const dt = Math.min(0.1, now - w.last);
      w.last = now;
      w.owed += 260 * dt * Math.min(1, swell * 2);
      const foam: Speck = { tex: 'puff', color: 1, rate: 0, burst: 0, size: 3.2, life: 0.9, spread: 0.3, speed: 1.6, rise: 3, add: true, grow: 1.2 };
      const spray: Speck = { tex: 'dot', color: 1, rate: 0, burst: 0, size: 0.8, life: 0.8, spread: 0.3, speed: 2.5, rise: 7, add: true };
      const water: Speck = { tex: 'puff', color: 0, rate: 0, burst: 0, size: 4, life: 1.1, spread: 0.3, speed: 1, rise: 1.5, add: true, grow: 1 };
      while (w.owed >= 1) {
        w.owed -= 1;
        const at = front.clone().addScaledVector(w.side, (Math.random() - 0.5) * w.across).setY(2.5 + Math.random() * 2);
        const r = Math.random();
        this.speck(shot, r < 0.45 ? foam : r < 0.75 ? water : spray, at, now, false);
      }
      // red lightning striking across the table as it passes
      const boltShot = { colors: [w.colors[2] ?? '#ff2a2a', w.colors[3] ?? '#ffb0a0'] } as unknown as Shot;
      const bolt: Speck = { tex: 'bolt', color: 0, rate: 0, burst: 0, size: 4.2 / Z, life: 0.35, spread: 0, speed: 0, rise: 0, anchor: 'top', aspect: 2.6, float: true, still: true, add: true };
      const glow: Speck = { tex: 'dot', color: 1, rate: 0, burst: 0, size: 4 / Z, life: 0.4, spread: 0, speed: 0, rise: 0, float: true, still: true, add: true };
      w.bolts = w.bolts.filter((at) => {
        if (k < at) return true;
        const spot = new THREE.Vector3((Math.random() - 0.5) * (Math.abs(w.dir.x) ? w.travel - 30 : w.across - 16), 0, (Math.random() - 0.5) * (Math.abs(w.dir.z) ? w.travel - 30 : w.across - 16));
        this.speck(boltShot, bolt, spot.clone().setY(13), now, false);
        this.speck(boltShot, glow, spot.clone().setY(0.6), now, false);
        const geo = new THREE.PlaneGeometry(1, 1);
        geo.rotateX(-Math.PI / 2);
        const mesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ map: textures().ring, color: boltShot.colors[0], transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }));
        mesh.position.copy(spot).setY(0.5);
        mesh.renderOrder = 18;
        this.scene.add(mesh);
        this.rings.push({ mesh, born: now, life: CONFIG.combat.attackFx.ringSeconds, size: CONFIG.combat.attackFx.ringSize * 1.4 });
        return false;
      });
    }
    this.waves = this.waves.filter((w) => {
      if (now - w.t0 < w.dur) return true;
      for (const m of w.crest) { this.scene.remove(m); m.geometry.dispose(); (m.material as THREE.Material).dispose(); }
      return false;
    });
  }

  private speck(shot: Shot, s: Speck, at: THREE.Vector3, now: number, burst: boolean, groundY?: number) {
    const t = textures();
    const color = shot.colors[Math.min(s.color, shot.colors.length - 1)];
    const upright = !!s.anchor; // (a root or a bolt stands straight, not turned at random)
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: t[s.tex], color, transparent: true, depthWrite: false, rotation: upright ? (Math.random() - 0.5) * 0.5 : Math.random() * 6.28, blending: s.add ? THREE.AdditiveBlending : THREE.NormalBlending }));
    if (s.anchor) sp.center.set(0.5, s.anchor === 'bottom' ? 0 : 1);
    const a = Math.random() * Math.PI * 2;
    const Z = CONFIG.combat.attackFx.size;
    const r = Math.random() * s.spread * Z;
    const y = s.ground && groundY !== undefined ? groundY : at.y + (s.anchor ? 0 : (Math.random() - 0.3) * s.spread * Z * 0.6);
    sp.position.set(at.x + Math.cos(a) * r, y, at.z + Math.sin(a) * r);
    const size = s.size * Z * (0.7 + Math.random() * 0.6);
    const aspect = s.aspect ?? 1;
    sp.scale.set(size, size * aspect, 1);
    sp.renderOrder = s.anchor === 'top' ? 23 : 19; // (lightning in front of its cloud)
    this.scene.add(sp);
    const v = s.still ? 0 : s.speed * Z * (burst ? 1.8 : 1) * (0.5 + Math.random() * 0.8);
    const vy = s.anchor && s.still ? 0 : s.rise * (0.6 + Math.random() * 0.8) + (burst && !s.float ? Math.abs(s.rise) + 1.5 : 0);
    this.parts.push({ sp, born: now, life: s.life * (burst ? 1.25 : 1) * (0.75 + Math.random() * 0.5), vx: Math.cos(a) * v, vy, vz: Math.sin(a) * v, size, grow: s.grow ?? 0, spin: upright ? 0 : (s.spin ?? 0) * (Math.random() < 0.5 ? -1 : 1), fade: s.add ? 1 : 0.9, aspect, float: !!s.float, emerge: !!s.emerge });
  }

  update(now: number) {
    const A = CONFIG.combat.attackFx;
    if (this.waves.length) this.updateWaves(now);
    const step = Math.min(0.1, Math.max(0, now - (this.lastTick || now)));
    this.lastTick = now;
    for (const shot of this.shots) {
      if (now < shot.t0) continue;
      const k = Math.min(1, (now - shot.t0) / shot.dur);
      const along = (q: number) => {
        // a storm stays over its target; everything else travels there, arcing up on the way
        if (shot.style.storm) return shot.to.clone().add(new THREE.Vector3(0, A.height + 4.5, 0));
        const p = shot.from.clone().lerp(shot.to, q);
        p.y += (shot.style.low ?? A.height) + Math.sin(q * Math.PI) * shot.style.arc;
        return p;
      };
      const pos = along(k);
      const groundY = shot.from.y + (shot.to.y - shot.from.y) * k + 0.05;
      if (shot.arrow) shot.arrow.lookAt(pos.clone().add(along(Math.min(1, k + 0.02)).sub(along(Math.max(0, k - 0.02)))));
      shot.group.visible = true;
      shot.group.position.copy(pos);
      const fade = shot.miss ? Math.max(0, 1 - Math.max(0, k - 0.55) / 0.45) : 1;
      const c = shot.style.core;
      if (shot.core && c) {
        const mat = (shot.core as THREE.Mesh | THREE.Sprite).material as THREE.Material & { rotation?: number };
        mat.opacity = fade;
        if (c.spin && 'rotation' in mat) mat.rotation = (now - shot.t0) * c.spin;
        if (c.flat) shot.core.scale.setScalar(0.55 + 0.6 * k); // a wave swells as it rolls in
      }
      if (shot.halo) shot.halo.material.opacity = 0.8 * fade;
      const o = shot.style.orbit;
      shot.orbit.forEach((sp, i) => {
        const a = (now - shot.t0) * o!.speed + (i / shot.orbit.length) * Math.PI * 2;
        sp.position.set(Math.cos(a) * o!.radius * A.size, Math.sin(a * 0.7 + i) * o!.radius * A.size * 0.45, Math.sin(a) * o!.radius * A.size);
        sp.material.opacity = fade;
        sp.material.rotation = a;
      });
      // what trails behind it
      const dt = Math.min(0.1, now - shot.last);
      shot.last = now;
      shot.style.specks.forEach((s, i) => {
        shot.owed[i] += s.rate * dt * fade;
        while (shot.owed[i] >= 1) {
          shot.owed[i] -= 1;
          this.speck(shot, s, pos, now, false, groundY);
        }
      });
      if (k >= 1 && !shot.landed) {
        shot.landed = true;
        if (!shot.miss) {
          for (const s of shot.style.specks) for (let n = 0; n < s.burst; n++) this.speck(shot, s, pos, now, true, shot.to.y + 0.05);
          const geo = new THREE.PlaneGeometry(1, 1);
          geo.rotateX(-Math.PI / 2);
          const mesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ map: textures().ring, color: shot.colors[Math.min(shot.style.ring, shot.colors.length - 1)], transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }));
          mesh.position.set(shot.to.x, shot.to.y + 0.5, shot.to.z);
          mesh.renderOrder = 18;
          this.scene.add(mesh);
          this.rings.push({ mesh, born: now, life: A.ringSeconds, size: A.ringSize });
          shot.onLand?.();
        }
      }
    }
    this.shots = this.shots.filter((shot) => {
      if (!shot.landed) return true;
      this.scene.remove(shot.group);
      shot.group.traverse((n) => {
        const m = (n as THREE.Mesh).material as THREE.Material | undefined;
        m?.dispose();
        (n as THREE.Mesh).geometry?.dispose();
      });
      return false;
    });
    this.parts = this.parts.filter((p) => {
      const age = (now - p.born) / p.life;
      if (age >= 1) {
        this.scene.remove(p.sp);
        p.sp.material.dispose();
        return false;
      }
      const dt = step;
      p.sp.position.x += p.vx * dt;
      p.sp.position.y += p.vy * dt;
      p.sp.position.z += p.vz * dt;
      if (!p.float) p.vy -= (p.vy > 6 ? 14 : 2.2) * dt; // everything sinks a little (a plume thrown high falls back fast)
      const size = p.size * (1 + p.grow * age);
      // (a root grows up out of the table, then sinks back into it)
      const tall = p.emerge ? Math.min(1, age * 4) * (age > 0.7 ? (1 - age) / 0.3 : 1) : 1;
      p.sp.scale.set(size, size * p.aspect * tall, 1);
      p.sp.material.rotation += p.spin * dt;
      p.sp.material.opacity = p.fade * (1 - age) * Math.min(1, age * 8);
      return true;
    });
    this.rings = this.rings.filter((r) => {
      if (now < r.born) return true; // (a ripple still to come)
      const age = (now - r.born) / r.life;
      if (age >= 1) {
        this.scene.remove(r.mesh);
        (r.mesh.material as THREE.Material).dispose();
        r.mesh.geometry.dispose();
        return false;
      }
      const size = r.size * (0.25 + 0.75 * (1 - Math.pow(1 - age, 3)));
      r.mesh.scale.set(size, 1, size);
      (r.mesh.material as THREE.MeshBasicMaterial).opacity = 1 - age;
      return true;
    });
  }

  dispose() {
    for (const shot of this.shots) this.scene.remove(shot.group);
    for (const p of this.parts) this.scene.remove(p.sp);
    for (const r of this.rings) this.scene.remove(r.mesh);
    this.shots = [];
    this.parts = [];
    this.rings = [];
  }
}
